import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
	value: T;
	label: ReactNode;
	/** Small count or dot after the label. */
	badge?: ReactNode;
	disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
	value: T;
	onValueChange: (value: T) => void;
	options: SegmentedOption<T>[];
	'aria-label': string;
	className?: string;
}

/**
 * iOS-style segmented control (Open | Closed, Groups | Direct messages):
 * equal-width segments on a grey track with a sliding white thumb. 36px tall
 * with a 44px hit area. Arrow keys move between segments.
 */
export function SegmentedControl<T extends string>({
	value,
	onValueChange,
	options,
	className,
	...aria
}: SegmentedControlProps<T>) {
	const index = Math.max(
		0,
		options.findIndex((o) => o.value === value),
	);

	const move = (step: number) => {
		for (let i = 1; i <= options.length; i++) {
			const next = options[(index + step * i + options.length) % options.length];
			if (!next.disabled) {
				onValueChange(next.value);
				return;
			}
		}
	};

	return (
		<div
			role="radiogroup"
			aria-label={aria['aria-label']}
			onKeyDown={(e) => {
				if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
					e.preventDefault();
					move(1);
				} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
					e.preventDefault();
					move(-1);
				}
			}}
			className={cn('relative grid h-9 rounded-[9px] bg-zinc-200/80 p-0.5 dark:bg-zinc-800', className)}
			style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
		>
			<span
				aria-hidden
				className="absolute inset-y-0.5 left-0.5 rounded-[7px] bg-background shadow-sm ring-1 ring-black/5 transition-transform duration-200 ease-out motion-reduce:transition-none dark:bg-zinc-600"
				style={{
					width: `calc((100% - 4px) / ${options.length})`,
					transform: `translateX(${index * 100}%)`,
				}}
			/>
			{options.map((option) => {
				const selected = option.value === value;
				return (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={selected}
						tabIndex={selected ? 0 : -1}
						disabled={option.disabled}
						onClick={() => onValueChange(option.value)}
						className={cn(
							'relative z-[1] flex min-w-0 items-center justify-center gap-1.5 rounded-[7px] px-2 text-[13px] font-medium outline-none transition-colors',
							'before:absolute before:-inset-y-1 before:inset-x-0 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40',
							selected ? 'text-foreground' : 'text-foreground/70',
						)}
					>
						<span className="truncate">{option.label}</span>
						{option.badge}
					</button>
				);
			})}
		</div>
	);
}
